import autoTable from "jspdf-autotable";
import { NUM, fmtDate, todayISO, unitLabel } from "@/lib/format";
import { createDoc, money, NAVY, INK, SLATE, BAND, WASH, LINE, GOLD } from "@/lib/docPdf";

export const SORTS = {
  brand: ["Marka bazlı", "Marka grupları, ara toplamlar · değere göre"],
  value: ["Ürün maliyetine göre", "En yüksek stok değeri en üstte"],
  qty: ["Stok adedine göre", "En çok stoklu ürün en üstte"],
  general: ["Genel liste", "Ürün adına göre A → Z"],
};
export const BASES = {
  list: ["Liste − iskonto", "Liste fiyatı × (1 − alış iskontosu)"],
  last: ["Son alış fiyatı", "Faturadan gelen son alış; yoksa liste − iskonto"],
};
const SORTERS = {
  // eşit değerde (ör. fiyatsız) stokta olanlar stoksuzlardan önce gelsin
  value: (k) => (a, b) => b[k] - a[k] || (b.quantity > 0) - (a.quantity > 0) || b.quantity - a.quantity,
  qty: () => (a, b) => b.quantity - a.quantity,
  general: () => (a, b) => String(a.name).localeCompare(String(b.name), "tr"),
};
const sum = (arr, f) => arr.reduce((s, r) => s + (Number(f(r)) || 0), 0);
const pos = (v) => Math.max(Number(v) || 0, 0);
const RED = [185, 28, 28];

export function summarize(items, kdv = true) {
  const vk = kdv ? "value_total" : "value", uk = kdv ? "unit_value" : "unit_cost";
  const map = new Map();
  items.forEach((r) => { const b = r.brand || "— Markasız —"; if (!map.has(b)) map.set(b, []); map.get(b).push(r); });
  const brands = [...map.entries()].map(([brand, rows]) => ({
    brand, rows, count: rows.length, value: sum(rows, (r) => r[vk]), out: rows.filter((r) => r.quantity <= 0).length,
    depo: sum(rows, (r) => pos(r.qty_depo)), mag: sum(rows, (r) => pos(r.qty_magaza)),
  })).sort((a, b) => b.value - a.value);
  return {
    vk, uk, brands, count: items.length,
    zero: items.filter((r) => r.quantity === 0).length, neg: items.filter((r) => r.quantity < 0).length,
    depo: sum(items, (r) => pos(r.qty_depo)), mag: sum(items, (r) => pos(r.qty_magaza)),
    value: sum(items, (r) => r.value), valueKdv: sum(items, (r) => r.value_total),
    depoVal: sum(items, (r) => pos(r.qty_depo) * r[uk]), magVal: sum(items, (r) => pos(r.qty_magaza) * r[uk]),
    fromLast: items.filter((r) => r.from_last).length,
  };
}

/** Stok Maliyet Raporu PDF — önce özet (KPI + marka özeti), sonra seçilen sıralamada Depo/Mağaza/Toplam ürün dökümü. */
export async function buildStockReportPdf({ items, sort = "brand", basis = "list", kdv = true, brandFilter = "", withOut = false }) {
  const S = summarize(items, kdv);
  const today = fmtDate(todayISO());
  const D = await createDoc({ title: "STOK RAPORU", subtitle: "MALİYET · DEPO / MAĞAZA", contLine: `${today}  ·  ${SORTS[sort][0]}  ·  devam` });
  const { pdf, F, T, M, W, HEAD1 } = D;
  D.header(1);
  let y = HEAD1 + 8;
  y = D.sectionTitle(y, "STOK ÖZETİ", `${today}  ·  ${BASES[basis][0]}  ·  ${kdv ? "KDV dahil" : "KDV hariç"}${withOut ? "  ·  Stokta olmayanlar dahil" : ""}${brandFilter ? `  ·  Marka: ${brandFilter}` : ""}`) + 1;

  const kpiRow = (yy, list) => {
    const gap = 2.5, bw = (W - gap * (list.length - 1)) / list.length, bh = 17;
    list.forEach(([label, val, sub, accent], i) => {
      const x = M + i * (bw + gap);
      pdf.setFillColor(...(accent ? NAVY : WASH)); pdf.roundedRect(x, yy, bw, bh, 1.6, 1.6, "F");
      if (!accent) { pdf.setDrawColor(...LINE); pdf.setLineWidth(0.25); pdf.roundedRect(x, yy, bw, bh, 1.6, 1.6, "S"); }
      T(F.body, "bold", 5.8, accent ? [203, 213, 225] : SLATE); pdf.text(label, x + 2.6, yy + 4.6, { charSpace: 0.3 });
      const fs = D.shrink(val, bw - 5, F.head, "bold", 11.5, 7);
      T(F.head, "bold", fs, accent ? GOLD : NAVY); pdf.text(val, x + 2.6, yy + 10.6);
      T(F.body, "normal", 5.8, accent ? [226, 232, 240] : SLATE); pdf.text(D.ellipsize(sub, bw - 5), x + 2.6, yy + 14.6);
    });
    return yy + bh + 2.5;
  };
  y = kpiRow(y, [
    ["ÜRÜN ÇEŞİDİ", NUM(S.count), withOut ? `${NUM(S.count - S.zero - S.neg)} stokta · ${NUM(S.zero)} yok · ${NUM(S.neg)} eksi` : `${S.brands.length} marka`],
    ["DEPO MİKTARI", NUM(S.depo), `değer ${money(S.depoVal)}`],
    ["MAĞAZA MİKTARI", NUM(S.mag), `değer ${money(S.magVal)}`],
    ["STOK MALİYETİ", money(S.value), "KDV hariç"],
    ["KDV DAHİL TOPLAM", money(S.valueKdv), `KDV ${money(S.valueKdv - S.value)}`, true],
  ]);
  T(F.body, "normal", 6.3, SLATE);
  pdf.text(pdf.splitTextToSize(`Birim maliyet: ${BASES[basis][1]}${kdv ? " + ürün KDV oranı (varsayılan %20)" : ""}. Yalnız stoğu pozitif ürünler değerlenir; eksi stoklar sıfır sayılır.${withOut ? " Stokta olmayan (0) ve eksi stoklu ürünler de listelenmiştir (kırmızı işaretli)." : ""}${basis === "last" ? ` ${S.fromLast} üründe son alış fiyatı kullanıldı.` : ""}`, W), M, y + 2.5, { lineHeightFactor: 1.35 });
  y += withOut ? 10 : 8;

  const top = S.brands.slice(0, 12);
  const grand = kdv ? S.valueKdv : S.value;
  const share = (v) => `%${grand > 0 ? NUM(Math.round((v / grand) * 1000) / 10) : 0}`;
  y = D.sectionTitle(D.ensure(y, 30) + 2, "MARKA ÖZETİ", `${S.brands.length > 12 ? `ilk 12 / ${S.brands.length} marka` : `${S.brands.length} marka`}  ·  stok değerine göre`);
  autoTable(pdf, D.tableBase({
    head: [["MARKA", { content: "ÜRÜN", styles: { halign: "right" } }, { content: "DEPO", styles: { halign: "right" } }, { content: "MAĞAZA", styles: { halign: "right" } }, { content: "STOK DEĞERİ", styles: { halign: "right" } }, { content: "PAY", styles: { halign: "right" } }]],
    body: top.map((b) => [b.brand, withOut && b.out ? `${NUM(b.count)} (${NUM(b.out)} yok)` : NUM(b.count), NUM(b.depo), NUM(b.mag), money(b.value), share(b.value)]),
    startY: y,
    headStyles: { font: F.body, fontStyle: "bold", fillColor: BAND, textColor: NAVY, fontSize: 6.6, lineWidth: 0 },
    columnStyles: { 0: { cellWidth: W - 24 - 22 - 22 - 34 - 16, fontStyle: "bold" }, 1: { cellWidth: 24, halign: "right" }, 2: { cellWidth: 22, halign: "right" }, 3: { cellWidth: 22, halign: "right" }, 4: { cellWidth: 34, halign: "right", fontStyle: "bold", textColor: NAVY }, 5: { cellWidth: 16, halign: "right", textColor: SLATE } },
    styles: { font: F.body, fontSize: 7.2, cellPadding: { top: 1.1, bottom: 1.1, left: 1.8, right: 1.8 }, textColor: INK, lineColor: LINE, lineWidth: 0.2 },
  }));
  y = pdf.lastAutoTable.finalY + 7;

  const row = (r, i) => {
    const out = r.quantity <= 0;
    const tag = r.quantity < 0 ? "EKSİ STOK" : out ? "STOKTA YOK" : "";
    const red = (content) => ({ content, styles: { textColor: RED, fontStyle: "bold" } });
    return [
      String(i),
      `${r.name}\n${[tag, r.code, sort === "brand" ? "" : r.brand, r.list_price ? `Liste ${money(r.list_price)} · −%${NUM(r.disc)}` : "", r.from_last ? "son alış fiyatı" : ""].filter(Boolean).join("  ·  ")}`,
      out ? red(NUM(r.qty_depo)) : NUM(r.qty_depo), out ? red(NUM(r.qty_magaza)) : NUM(r.qty_magaza),
      out ? red(`${NUM(r.quantity)} ${unitLabel(r.unit)}`) : `${NUM(r.quantity)} ${unitLabel(r.unit)}`,
      money(r[S.uk]), out ? { content: "—", styles: { textColor: SLATE } } : money(r[S.vk]),
    ];
  };
  const body = [];
  let n = 0;
  if (sort === "brand") {
    S.brands.forEach((b) => {
      body.push([{ content: `${b.brand}   ·   ${b.count} ürün${withOut && b.out ? ` (${b.out} stoksuz)` : ""}   ·   Depo ${NUM(b.depo)}   ·   Mağaza ${NUM(b.mag)}   ·   ${money(b.value)}`, colSpan: 7, styles: { fillColor: BAND, textColor: NAVY, fontStyle: "bold", fontSize: 7.4 } }]);
      [...b.rows].sort(SORTERS.value(S.vk)).forEach((r) => body.push(row(r, ++n)));
    });
  } else {
    [...items].sort((SORTERS[sort] || SORTERS.value)(S.vk)).forEach((r) => body.push(row(r, ++n)));
  }
  y = D.sectionTitle(D.ensure(y, 30), "ÜRÜN DÖKÜMÜ", `${SORTS[sort][0]}  ·  ${SORTS[sort][1]}`);
  autoTable(pdf, D.tableBase({
    head: [[{ content: "#", styles: { halign: "center" } }, "ÜRÜN", { content: "DEPO", styles: { halign: "right" } }, { content: "MAĞAZA", styles: { halign: "right" } }, { content: "TOPLAM", styles: { halign: "right" } }, { content: kdv ? "BİRİM (KDV'Lİ)" : "BİRİM MALİYET", styles: { halign: "right" } }, { content: "STOK DEĞERİ", styles: { halign: "right" } }]],
    body: body.length ? body : [[{ content: "Raporlanacak ürün yok", colSpan: 7, styles: { halign: "center", textColor: SLATE } }]],
    startY: y,
    columnStyles: {
      0: { cellWidth: 9, halign: "center", textColor: SLATE, fontSize: 6.6 },
      1: { cellWidth: W - 9 - 16 - 16 - 21 - 25 - 28 },
      2: { cellWidth: 16, halign: "right" },
      3: { cellWidth: 16, halign: "right", textColor: [15, 118, 110] },
      4: { cellWidth: 21, halign: "right", fontStyle: "bold" },
      5: { cellWidth: 25, halign: "right" },
      6: { cellWidth: 28, halign: "right", fontStyle: "bold", textColor: NAVY },
    },
    ...D.cellHooks(1, 7.4, "bold"),
  }));
  y = pdf.lastAutoTable.finalY + 6;

  const tRows = [["ÜRÜN ÇEŞİDİ", withOut ? `${NUM(S.count)} (${NUM(S.zero + S.neg)} stoksuz)` : NUM(S.count)], ["DEPO / MAĞAZA", `${NUM(S.depo)} / ${NUM(S.mag)}`], ["KDV HARİÇ", money(S.value), true], ["KDV", money(S.valueKdv - S.value)]];
  y = D.ensure(y, D.totalsHeight(tRows.length) + 2);
  D.totalsBox(y, tRows, kdv ? S.valueKdv : S.value, { pos: "TOPLAM STOK DEĞERİ", posSub: kdv ? "KDV DAHİL" : "KDV HARİÇ", neg: "TOPLAM STOK DEĞERİ", negSub: "" });
  return D.finish();
}
